import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const CalculatorForm = ({ onCalculate, currentLanguage }) => {
  const [savingsAmount, setSavingsAmount] = useState('');
  const [monthlyExpenses, setMonthlyExpenses] = useState('');
  const [errors, setErrors] = useState({});

  // Format number with commas
  const formatNumber = (value) => {
    if (!value) return '';
    const number = value?.replace(/,/g, '');
    return number?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Parse formatted number to float
  const parseFormattedNumber = (value) => {
    return parseFloat(value?.replace(/,/g, '')) || 0;
  };

  const translations = {
    en: {
      savingsLabel: "Total Savings Amount",
      savingsPlaceholder: "Enter your total savings",
      expensesLabel: "Monthly Expenses",
      expensesPlaceholder: "Enter your monthly expenses",
      calculateButton: "Calculate Duration",
      errorRequired: "This field is required",
      errorPositive: "Amount must be greater than zero"
    },
    es: {
      savingsLabel: "Monto Total de Ahorros",
      savingsPlaceholder: "Ingrese sus ahorros totales",
      expensesLabel: "Gastos Mensuales",
      expensesPlaceholder: "Ingrese sus gastos mensuales",
      calculateButton: "Calcular Duración",
      errorRequired: "Este campo es obligatorio",
      errorPositive: "El monto debe ser mayor que cero"
    },
    pt: {
      savingsLabel: "Valor Total de Poupança",
      savingsPlaceholder: "Digite sua poupança total",
      expensesLabel: "Despesas Mensais",
      expensesPlaceholder: "Digite suas despesas mensais",
      calculateButton: "Calcular Duração",
      errorRequired: "Este campo é obrigatório",
      errorPositive: "O valor deve ser maior que zero"
    },
    fr: {
      savingsLabel: "Montant Total d\'Épargne",
      savingsPlaceholder: "Entrez votre épargne totale",
      expensesLabel: "Dépenses Mensuelles",
      expensesPlaceholder: "Entrez vos dépenses mensuelles",
      calculateButton: "Calculer la Durée",
      errorRequired: "Ce champ est obligatoire",
      errorPositive: "Le montant doit être supérieur à zéro"
    },
    de: {
      savingsLabel: "Gesamtersparnis",
      savingsPlaceholder: "Geben Sie Ihre Gesamtersparnis ein",
      expensesLabel: "Monatliche Ausgaben",
      expensesPlaceholder: "Geben Sie Ihre monatlichen Ausgaben ein",
      calculateButton: "Dauer Berechnen",
      errorRequired: "Dieses Feld ist erforderlich",
      errorPositive: "Der Betrag muss größer als Null sein"
    }
  };

  const t = translations?.[currentLanguage] || translations?.en;

  const validateForm = () => {
    const newErrors = {};

    const savingsValue = parseFormattedNumber(savingsAmount);
    const expensesValue = parseFormattedNumber(monthlyExpenses);

    if (!savingsAmount || savingsAmount?.trim() === '') {
      newErrors.savingsAmount = t?.errorRequired;
    } else if (savingsValue <= 0) {
      newErrors.savingsAmount = t?.errorPositive;
    }

    if (!monthlyExpenses || monthlyExpenses?.trim() === '') {
      newErrors.monthlyExpenses = t?.errorRequired;
    } else if (expensesValue <= 0) {
      newErrors.monthlyExpenses = t?.errorPositive;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onCalculate({
        savings: parseFormattedNumber(savingsAmount),
        expenses: parseFormattedNumber(monthlyExpenses)
      });
    }
  };

  const handleSavingsChange = (e) => {
    const value = e?.target?.value?.replace(/[^0-9.]/g, '');
    setSavingsAmount(formatNumber(value));
    if (errors?.savingsAmount) {
      setErrors(prev => ({ ...prev, savingsAmount: '' }));
    }
  };

  const handleExpensesChange = (e) => {
    const value = e?.target?.value?.replace(/[^0-9.]/g, '');
    setMonthlyExpenses(formatNumber(value));
    if (errors?.monthlyExpenses) {
      setErrors(prev => ({ ...prev, monthlyExpenses: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto">
      <div className="space-y-6 md:space-y-6">
        <Input
          type="text"
          inputMode="numeric"
          label={t?.savingsLabel}
          placeholder={t?.savingsPlaceholder}
          value={savingsAmount}
          onChange={handleSavingsChange}
          error={errors?.savingsAmount}
          required
          className="w-full"
        />

        <Input
          type="text"
          inputMode="numeric"
          label={t?.expensesLabel}
          placeholder={t?.expensesPlaceholder}
          value={monthlyExpenses}
          onChange={handleExpensesChange}
          error={errors?.monthlyExpenses}
          required
          className="w-full"
        />

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          iconName="Calculator"
          iconPosition="left"
          className="h-12 md:h-10 text-base md:text-sm"
        >
          {t?.calculateButton}
        </Button>
      </div>
    </form>
  );
};

export default CalculatorForm;